import { useCallback, useEffect, useRef, useState } from 'react';
import { datasetsApi, rawApi, georefApi } from '../lib/api';
import { chooseLayerMode } from '../lib/vector-tiles';
import { boreholePopupProps } from '../lib/boreholes';
import { isRasterDataset } from '../lib/raster-api';
import { isVectorFile } from '../lib/dataset-kind';
import { extraLayerIds } from '../lib/map-url';
import { useViewSync } from '../contexts/ViewSyncContext';

/**
 * Dataset-backed map layers: electrodes and pseudosection of a parsed raw survey
 * (`/map?dataset=<id>`). The layers go into MapView's resultLayers list so they
 * render, toggle and remove like the other result layers, and appear in the
 * "Dataset layers" section of the layer panel.
 *
 * A dataset without `metadata.georef` is in local survey coordinates, which would
 * land at 0 deg, 0 deg; no layer is added until a georef exists (banner + GeorefDialog).
 */

const RAMP = ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#f87171']; // cool -> warm, as xyzPaint
const INK = '#0b1220';

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/** The endpoints the layers load from (also shown as the layer's source). */
export function rawLayerUrls(datasetId) {
  const base = `/api/datasets/${datasetId}/raw`;
  return { electrodes: `${base}/electrodes`, pseudosection: `${base}/pseudosection?qc=1` };
}

/**
 * A plain vector/tabular file (GeoJSON, KML, GPX, CSV, XYZ) that is not a parsed resistivity
 * survey (lib/dataset-kind.js). Its coordinates are already geographic (or mapped on load), so
 * it needs no georef: hooks/useProjectDatasetLayers loads it for `?dataset=`.
 */
export function isVectorFileDataset(ds) {
  return !!ds && !isRasterDataset(ds) && isVectorFile(ds);
}

/** A dataset is placed on the map only when it carries a non-local georef. */
export function hasGeoref(metadata) {
  const g = metadata?.georef;
  return !!g && !!g.crs && String(g.crs).toLowerCase() !== 'local';
}

/** Resistivity ramp stops: 5 values evenly spaced in log10 between the 2nd and 98th percentile. */
export function rhoStops(values) {
  const v = values.filter(x => Number.isFinite(x) && x > 0).sort((a, b) => a - b);
  if (!v.length) return [1, 10, 100, 1000, 10000];
  const q = (p) => v[Math.min(v.length - 1, Math.max(0, Math.round(p * (v.length - 1))))];
  let lo = Math.log10(q(0.02)), hi = Math.log10(q(0.98));
  if (!(hi > lo)) hi = lo + 1;
  return [0, 1, 2, 3, 4].map(i => 10 ** (lo + ((hi - lo) * i) / 4));
}

/** The colour expression: apparent resistivity (log scale) on the cool-to-warm ramp. */
export function rhoColorExpr(stops) {
  const x = ['log10', ['max', ['to-number', ['get', 'apparent_resistivity'], stops[0]], 1e-6]];
  const expr = ['interpolate', ['linear'], x];
  stops.forEach((s, i) => expr.push(Math.log10(s), RAMP[i]));
  return expr;
}

/**
 * Paint for the pseudosection layer: colour by apparent resistivity; the QC tier is a
 * secondary style (tier D points are hollow rings, A-C solid with a dark outline).
 */
export function pseudosectionPaint(features) {
  const color = rhoColorExpr(rhoStops(features.map(f => f.properties?.apparent_resistivity)));
  const hollow = ['==', ['get', 'tier'], 'D'];
  return {
    'circle-radius': 4,
    'circle-color': ['case', hollow, 'rgba(0,0,0,0)', color],
    'circle-opacity': 0.9,
    'circle-stroke-color': ['case', hollow, color, INK],
    'circle-stroke-width': ['case', hollow, 1.5, 0.5],
  };
}

export const electrodePaint = () => ({
  'circle-radius': 4.5,
  'circle-color': ['case', ['==', ['get', 'remote'], true], '#a78bfa', '#ffffff'],
  'circle-opacity': 0.95,
  'circle-stroke-color': INK,
  'circle-stroke-width': 1.2,
});

const round = (v, n) => (typeof v === 'number' && Number.isFinite(v) ? Number(v.toPrecision(n)) : v);

/** Row shown in the click popup (key order = display order). */
export function rawPopupProps(kind, p = {}) {
  if (kind === 'boreholes') return boreholePopupProps(p); // hooks/useBoreholeLayers
  if (kind === 'electrodes') {
    return { electrode: p.id, remote: p.remote ? 'yes' : 'no', local_x: p.local_x, local_y: p.local_y, local_z: p.local_z };
  }
  const flags = Array.isArray(p.flags) ? p.flags : (typeof p.flags === 'string' && p.flags.startsWith('[') ? JSON.parse(p.flags) : []);
  return {
    reading: p.seq, 'A B M N': [p.a, p.b, p.m, p.n].join(' '), array: p.array,
    apparent_resistivity: round(p.apparent_resistivity, 4), pseudo_depth: round(p.pseudo_depth, 3),
    error_pct: round(p.error_pct, 3), qc_tier: p.tier, flags: flags.length ? flags.join(', ') : 'none',
  };
}

/** [[minLon, minLat], [maxLon, maxLat]] of the point features, or null. */
export function featureBounds(featureCollections) {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  featureCollections.forEach(fc => (fc?.features || []).forEach(f => {
    const c = f.geometry?.coordinates;
    if (!c || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
    minLon = Math.min(minLon, c[0]); maxLon = Math.max(maxLon, c[0]);
    minLat = Math.min(minLat, c[1]); maxLat = Math.max(maxLat, c[1]);
  }));
  return Number.isFinite(minLon) ? [[minLon, minLat], [maxLon, maxLat]] : null;
}

/** The two result-layer entries for a loaded dataset. */
export function buildDatasetLayers(datasetId, name, electrodes, pseudosection) {
  const urls = rawLayerUrls(datasetId);
  const label = name || datasetId;
  return [
    { id: `ds-${datasetId}-electrodes`, name: `Electrodes — ${label}`, geojson: electrodes, kind: 'raw-electrodes', visible: true,
      paint: electrodePaint(), dataset: { id: datasetId, kind: 'electrodes', url: urls.electrodes, name: label } },
    { id: `ds-${datasetId}-pseudosection`, name: `Pseudosection — ${label}`, geojson: pseudosection, kind: 'raw-pseudosection', visible: true,
      paint: pseudosectionPaint(pseudosection.features || []), dataset: { id: datasetId, kind: 'pseudosection', url: urls.pseudosection, name: label } },
  ];
}

// ── Hook ────────────────────────────────────────────────────────────────────

/**
 * status: 'idle' (no ?dataset=) | 'loading' | 'needs-georef' | 'ready' | 'error'
 */
export function useDatasetRawLayers({ searchParams, setResultLayers, mapRefHolder }) {
  const datasetId = searchParams.get('dataset');
  const extraKey = extraLayerIds(searchParams).join(',');
  const [status, setStatus] = useState(datasetId ? 'loading' : 'idle');
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [georefInfo, setGeorefInfo] = useState(null); // data for GeorefDialog; non-null = dialog open
  const [georefTarget, setGeorefTarget] = useState(null); // { id, name, initialMode } the dialog edits (default: the ?dataset= one)
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const { syncCamera } = useViewSync();
  const syncCameraRef = useRef(false);
  syncCameraRef.current = syncCamera;

  const fitTo = useCallback((bounds) => {
    if (!bounds || syncCameraRef.current) return; // Sync camera: the synced camera wins over the zoom-to-fit (TODO P.20)
    let tries = 0;
    const t = setInterval(() => {
      const map = mapRefHolder?.current?.current?.getMap?.();
      if (map) {
        clearInterval(t);
        if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) map.jumpTo({ center: bounds[0], zoom: 17 });
        else map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 19 });
      } else if (++tries > 40) clearInterval(t);
    }, 150);
  }, [mapRefHolder]);

  const loadLayers = useCallback(async (id, datasetName, { quiet = false, fit = false } = {}) => {
    if (!quiet) { setStatus('loading'); setError(null); }
    try {
      const [el, ps] = await Promise.all([
        rawApi.electrodes(id),
        rawApi.pseudosectionAll(id, { qc: 1 }),
      ]);
      if (!mounted.current) return;
      const layers = buildDatasetLayers(id, datasetName, el.data, ps.data);
      setResultLayers(prev => [...prev.filter(r => r.dataset?.id !== id), ...layers]);
      if (quiet) { if (fit) fitTo(featureBounds([el.data, ps.data])); return; }
      setStatus('ready');
      fitTo(featureBounds([el.data, ps.data]));
    } catch (e) {
      if (!mounted.current || quiet) return;
      setError(e?.response?.data?.detail || e?.message || 'Could not load this dataset on the map');
      setStatus('error');
    }
  }, [setResultLayers, fitTo]);

  useEffect(() => {
    if (!datasetId) { setStatus('idle'); return undefined; }
    let active = true;
    setStatus('loading');
    datasetsApi.get(datasetId)
      .then(res => {
        if (!active) return;
        const ds = res.data || {};
        setName(ds.name || '');
        if (isRasterDataset(ds)) { setStatus('idle'); return; } // hooks/useRasterLayers handles it
        if (isVectorFileDataset(ds)) { setStatus('idle'); return; } // hooks/useProjectDatasetLayers loads it
        if (!hasGeoref(ds.metadata)) { setStatus('needs-georef'); return; }
        // Large dataset with ready tiles: hooks/useVectorTileLayers draws it (docs/VECTOR_TILES.md).
        if (chooseLayerMode(ds.metadata) === 'vector') { setStatus('vector'); return; }
        loadLayers(datasetId, ds.name);
      })
      .catch(e => {
        if (!active) return;
        setError(e?.response?.data?.detail || 'Dataset not found');
        setStatus('error');
      });
    return () => { active = false; };
  }, [datasetId, loadLayers]);

  // Datasets stacked with ?layers= (Insert > Open > Add): a georeferenced survey adds its
  // electrodes and pseudosection quietly (no camera move, no banner); other kinds are handled by
  // their own hooks. An id that leaves the list takes its layers with it.
  const loadedExtras = useRef([]);
  useEffect(() => {
    const ids = extraKey ? extraKey.split(',') : [];
    const gone = loadedExtras.current.filter((id) => !ids.includes(id));
    if (gone.length) setResultLayers((prev) => prev.filter((r) => !gone.includes(r.dataset?.id)));
    const fresh = ids.filter((id) => !loadedExtras.current.includes(id));
    loadedExtras.current = ids;
    fresh.forEach((id) => {
      datasetsApi.get(id).then((res) => {
        const ds = res.data || {};
        if (isRasterDataset(ds) || isVectorFileDataset(ds) || !hasGeoref(ds.metadata)) return;
        if (chooseLayerMode(ds.metadata) === 'vector') return;
        loadLayers(id, ds.name, { quiet: true });
      }).catch(() => {});
    });
  }, [extraKey, loadLayers, setResultLayers]);

  // Open the georef dialog for any dataset on the map (the layer menu); `initialMode` picks the tab
  // ('origin_azimuth' for Rotate). The banner button opens it for the ?dataset= one.
  const openGeorefFor = useCallback(async (id, datasetName, initialMode) => {
    try {
      const res = await georefApi.get(id);
      setGeorefTarget({ id, name: datasetName, initialMode });
      setGeorefInfo(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not read electrode geometry from this dataset');
    }
  }, []);
  const openGeoref = useCallback(() => openGeorefFor(datasetId, name), [openGeorefFor, datasetId, name]);

  const saveGeoref = useCallback(async (_georef, spec) => {
    const target = georefTarget || { id: datasetId, name };
    await georefApi.save(target.id, spec);
    setGeorefInfo(null);
    setGeorefTarget(null);
    await loadLayers(target.id, target.name, target.id === datasetId ? {} : { quiet: true, fit: true });
  }, [georefTarget, datasetId, name, loadLayers]);

  const closeGeoref = useCallback(() => { setGeorefInfo(null); setGeorefTarget(null); }, []);

  return { datasetId, name, status, error, georefInfo, georefTarget, openGeoref, openGeorefFor, saveGeoref, closeGeoref };
}
